import React, { useMemo, useState } from 'react';
import { SHORT_INTEREST_RANGE_OPTIONS } from './types';
import type { ShortInterestWidgetInstanceConfig, ShortInterestData, ShortInterestHistoryRow } from './types';
import {
  formatCompact,
  formatFloat,
  formatPercent,
  formatDate,
  axisCompact,
  axisMonthYear,
  buildQuarterlyTicks,
  DEFAULT_COLORS,
  WidgetEmptyState,
} from '../../../design';
import { useDataProviderWidget } from '../../../blocks/hooks';
import { ChartWidget, ChartRangeSelector, KeyMetricBadge, sliceByMonths, redundantRangeValues } from '../../../blocks/chart-widget';
import type { ChartSpec } from '../../../blocks/chart-widget';
import type { LineChartSeries, ChartLegendSeries } from '../../../blocks/charts';

// Sample data shown during dashboard construction (no asset assigned).
const SAMPLE_DATA: ShortInterestData = {
  ticker: 'SAMPLE',
  company_name: 'Sample Corp',
  settlement_date: '2025-03-14',
  shares_short: 34636195,
  shares_short_prev: 39059449,
  change_pct: -11.32,
  avg_daily_volume: 38275532,
  days_to_cover: 1.4,
  market_cap: 2_500_000_000,
  close_price: 182.5,
  adtv_3m: 43642924,
  shares_outstanding: 14687356000,
  si_pct_outstanding: 0.00236,
  short_interest_history: [
    { date: '2025-01-15', shares_short: 41200000, shares_short_prev: 40000000, change_pct: 3.0, avg_daily_volume: 38000000, days_to_cover: 1.1 },
    { date: '2025-01-31', shares_short: 39059449, shares_short_prev: 41200000, change_pct: -5.2, avg_daily_volume: 38500000, days_to_cover: 1.0 },
    { date: '2025-02-14', shares_short: 37500000, shares_short_prev: 39059449, change_pct: -4.0, avg_daily_volume: 39000000, days_to_cover: 1.2 },
    { date: '2025-02-28', shares_short: 35900000, shares_short_prev: 37500000, change_pct: -4.3, avg_daily_volume: 39500000, days_to_cover: 1.3 },
    { date: '2025-03-14', shares_short: 34636195, shares_short_prev: 35900000, change_pct: -3.5, avg_daily_volume: 38275532, days_to_cover: 1.4 },
  ],
  price_history: [
    { date: '2025-01-15', price: 168.2 },
    { date: '2025-01-31', price: 172.6 },
    { date: '2025-02-14', price: 176.1 },
    { date: '2025-02-28', price: 179.8 },
    { date: '2025-03-14', price: 182.5 },
  ],
};

// The left-axis metric is selectable (the deck's series tabs); the stock price
// stays overlaid on the right axis — "shares short vs stock price".
// si_pct_out is THE short-interest number (shares short ÷ shares outstanding);
// the other two are its ingredients.
type ShortInterestMetric = 'shares_short' | 'days_to_cover' | 'si_pct_out';

const SI_METRIC_OPTIONS: Array<{ value: ShortInterestMetric; label: string; title?: string }> = [
  { value: 'shares_short', label: 'Shares Short' },
  { value: 'days_to_cover', label: 'Days to Cover' },
  { value: 'si_pct_out', label: 'SI % Out',
    title: 'Shares short ÷ shares outstanding (as of today — historical share counts not yet sourced)' },
];

interface ShortInterestWidgetRendererProps {
  config: ShortInterestWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

const ShortInterestWidgetRenderer: React.FC<ShortInterestWidgetRendererProps> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useDataProviderWidget<ShortInterestData>(
    'short_interest',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const displayData = uid ? data : SAMPLE_DATA;
  const [range, setRange] = useState<string>(config.defaultRange ?? 'ALL');
  const [metric, setMetric] = useState<ShortInterestMetric>('shares_short');

  // Daily price rows drive the x axis (the price line must be daily, not
  // sampled at settlements); each bi-monthly FINRA settlement attaches to the
  // last trading day at or before its date and stays NULL in between — the
  // step interpolation holds the published value until the next print rather
  // than drawing a connect-the-dots slope through unpublished dates.
  const fullChartData = useMemo(() => {
    const priceRows = (displayData?.price_history ?? [])
      .filter((row) => typeof row.price === 'number')
      .sort((a, b) => a.date.localeCompare(b.date));

    const siRows = (displayData?.short_interest_history ?? [])
      .filter((row): row is ShortInterestHistoryRow & { shares_short: number } => typeof row.shares_short === 'number')
      .sort((a, b) => a.date.localeCompare(b.date));

    // SI % Out divides by TODAY's share count — an approximation over the
    // ~1y FINRA window (share counts drift slowly); historical counts are a
    // follow-up. Stored in percentage points (1.78 = 1.78%).
    const sharesOut = displayData?.shares_outstanding;
    const pctOut = (shares: number): number | null =>
      typeof sharesOut === 'number' && sharesOut > 0 ? (shares / sharesOut) * 100 : null;

    // No settlements at all: return NOTHING rather than a price-only chart —
    // this is the Short Interest widget, and an SI-less render must hit the
    // "No short interest reported" empty state, not show an unrelated
    // full-history price line.
    if (siRows.length === 0) return [];

    // No daily prices: fall back to settlement-only rows.
    if (priceRows.length === 0) {
      return siRows.map((row) => ({
        date: row.date,
        shares_short: row.shares_short,
        days_to_cover: typeof row.days_to_cover === 'number' ? row.days_to_cover : null,
        si_pct_out: pctOut(row.shares_short),
        price: null as number | null,
      }));
    }

    // Trim the daily axis to the SI coverage window so a 20-year price
    // history can't dwarf two years of settlements.
    const dailyRows = priceRows.filter((r) => r.date >= siRows[0].date);

    const siByDate = new Map<string, ShortInterestHistoryRow>();
    let p = 0;
    for (const row of siRows) {
      while (p + 1 < dailyRows.length && dailyRows[p + 1].date <= row.date) p += 1;
      if (dailyRows.length > 0 && dailyRows[p].date <= row.date) {
        siByDate.set(dailyRows[p].date, row);
      }
    }

    return dailyRows.map((r) => {
      const si = siByDate.get(r.date);
      return {
        date: r.date,
        shares_short: si?.shares_short ?? null,
        days_to_cover: typeof si?.days_to_cover === 'number' ? si.days_to_cover : null,
        si_pct_out: typeof si?.shares_short === 'number' ? pctOut(si.shares_short) : null,
        price: r.price as number | null,
      };
    });
  }, [displayData]);

  const hasPrice = useMemo(
    () => fullChartData.some((row) => typeof row.price === 'number'),
    [fullChartData],
  );

  const disabledRanges = useMemo(
    () => redundantRangeValues(SHORT_INTEREST_RANGE_OPTIONS, fullChartData, 'date'),
    [fullChartData],
  );

  const chartData = useMemo(() => {
    const months = SHORT_INTEREST_RANGE_OPTIONS.find((o) => o.value === range)?.months;
    return sliceByMonths(fullChartData, 'date', months);
  }, [fullChartData, range]);

  // Explicit date gridlines: ~quarterly on long windows, monthly on short
  // ones, with month+year labels ("Mar '25") so the years are on the axis.
  const dates = useMemo(() => chartData.map((d) => d.date), [chartData]);
  const xTicks = useMemo(() => {
    if (dates.length < 2) return undefined;
    const spanDays =
      (new Date(dates[dates.length - 1]).getTime() - new Date(dates[0]).getTime()) / 86_400_000;
    return buildQuarterlyTicks(dates, spanDays > 250 ? 90 : 30);
  }, [dates]);

  const metricLabel = SI_METRIC_OPTIONS.find((o) => o.value === metric)?.label ?? metric;
  const formatMetricValue = (value: number | null | undefined): string | null => {
    if (value == null) return null;
    if (metric === 'shares_short') return formatCompact(value);
    if (metric === 'si_pct_out') return formatPercent(value, 2);
    return formatFloat(value, 1);
  };

  // Selected short metric on the left axis, stock price overlaid on the right
  // ("shares short vs stock price"); toggle the left metric via the tabs.
  // The SI metric steps (boxy — value holds until the next settlement) with
  // markers on the actual settlement points; price is a smooth daily line.
  const series = useMemo<LineChartSeries[]>(() => {
    const result: LineChartSeries[] = [
      {
        yKey: metric, yName: metricLabel, axis: 'l', color: DEFAULT_COLORS[0],
        interpolation: 'step', connectMissingData: true, markers: true,
        // Filled pillars under the steps — the Hershey-Excel look.
        fill: true, fillOpacity: 0.25,
      },
    ];
    if (hasPrice) {
      result.push({ yKey: 'price', yName: 'Price', axis: 'r', color: DEFAULT_COLORS[1], markers: false });
    }
    return result;
  }, [hasPrice, metric, metricLabel]);

  // Last NON-NULL per key: on daily rows the final row usually has a price
  // but no settlement yet.
  const lastNonNull = (key: ShortInterestMetric | 'price'): number | null => {
    for (let i = chartData.length - 1; i >= 0; i -= 1) {
      const v = chartData[i][key];
      if (typeof v === 'number') return v;
    }
    return null;
  };

  const legendSeries = useMemo<ChartLegendSeries[]>(() => {
    const result: ChartLegendSeries[] = [
      { key: metric, name: metricLabel, color: DEFAULT_COLORS[0], value: formatMetricValue(lastNonNull(metric)) },
    ];
    if (hasPrice) {
      const lastPrice = lastNonNull('price');
      result.push({ key: 'price', name: 'Price', color: DEFAULT_COLORS[1], value: lastPrice != null ? formatFloat(lastPrice, 2) : null });
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, hasPrice, metric, metricLabel]);

  const spec: ChartSpec = {
    chartKind: 'line',
    rows: chartData,
    xKey: 'date',
    lineSeries: series,
    xLabelFormatter: axisMonthYear,
    xTickValues: xTicks,
    xTitle: 'Date',
    yTitles: {
      l: metric === 'si_pct_out' ? 'SI % of Shares Out' : metricLabel,
      r: 'Price ($)',
    },
    yLabelFormatters: {
      l: metric === 'shares_short'
        ? axisCompact
        : metric === 'si_pct_out'
          ? (params) => formatPercent(Number(params.value), 1)
          : (params) => formatFloat(Number(params.value), 1),
      r: (params) => formatFloat(Number(params.value), 0),
    },
    legendSeries,
    showEndValueTags: true,
    endValueFormatter: metric === 'shares_short'
      ? formatCompact
      : metric === 'si_pct_out'
        ? (value) => formatPercent(Number(value), 2)
        : (value) => formatFloat(Number(value), 1),
    footnote: 'Source: FINRA',
    emptyMessage: 'No short-interest history available',
  };

  // SI % of outstanding: show the value, or an honest reason when it could not
  // be computed (SEC shares-outstanding missing and no market-cap fallback).
  const siPct = displayData?.si_pct_outstanding;
  const siPctDisplay = typeof siPct === 'number' ? formatPercent(siPct * 100, 2) : null;
  const sharesOut = displayData?.shares_outstanding;

  const header = (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-neutral-100 shrink-0">
      <div className="min-w-0 mr-1 flex-1">
        <div
          className="text-sm font-semibold text-neutral-900 truncate"
          title={displayData?.company_name || displayData?.ticker || 'Short Interest'}
        >
          {displayData?.company_name || displayData?.ticker || 'Short Interest'}
        </div>
        {displayData?.settlement_date && (
          <div className="text-[11px] text-neutral-400">
            <span className="uppercase tracking-wide">As of </span>
            {formatDate(displayData.settlement_date)}
          </div>
        )}
      </div>
      <KeyMetricBadge label="Shares Short" value={formatCompact(displayData?.shares_short ?? null)} />
      <KeyMetricBadge label="Days to Cover" value={displayData?.days_to_cover != null ? formatFloat(displayData.days_to_cover, 1) : '--'} />
      <KeyMetricBadge
        label="SI % Out"
        value={siPctDisplay ?? (sharesOut == null ? 'n/a' : '--')}
        title={siPctDisplay ? undefined : 'Shares outstanding unavailable (SEC + market-cap fallback missing)'}
      />
      <div className="ml-auto flex items-center gap-2">
        <div className="flex items-center gap-1 rounded-xl border border-neutral-200 bg-neutral-50 p-1">
          {SI_METRIC_OPTIONS.map((option) => {
            // SI % Out needs a share-count denominator to exist at all.
            if (option.value === 'si_pct_out' && displayData?.shares_outstanding == null) return null;
            const isActive = metric === option.value;
            return (
              <button
                key={option.value}
                type="button"
                title={option.title}
                onClick={() => setMetric(option.value)}
                className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        <ChartRangeSelector
          options={SHORT_INTEREST_RANGE_OPTIONS}
          value={range}
          onChange={setRange}
          disabledValues={disabledRanges}
        />
      </div>
    </div>
  );

  // Fully empty (real asset, loaded, but FINRA returned nothing): one clean empty
  // state instead of a header full of "--" tiles plus a "no history" chart message.
  // Checked against the FULL history so a narrow range can't fake an empty widget.
  const hasData =
    typeof displayData?.shares_short === 'number' || fullChartData.length > 0;
  if (uid && !loading && !error && !hasData) {
    return (
      <WidgetEmptyState
        title="No short interest reported"
        description="FINRA has no short-interest data for this security."
      />
    );
  }

  return (
    <ChartWidget
      loading={loading}
      error={error}
      refetch={refetch}
      isPreview={isPreview}
      spec={spec}
      header={header}
    />
  );
};

export default ShortInterestWidgetRenderer;
